import { IconButton } from "@somethings-ui/web";
import { PanelLeftOpen } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, type RefObject } from "react";
import { ChatPopup } from "@/components/floating-chat/chat-popup";
import { useShouldShowFloatingChat } from "@/lib/hooks/admin/use-should-show-floating-chat";
import { useChannelsList } from "@/lib/hooks/conversations/use-channels-list";
import { usePopupStore } from "@/lib/hooks/conversations/use-popup-store";
import { INTERNAL_OPS_CUSTOM_TYPES } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { useMentorWorkflowsSelection } from "../use-selection";
import { useNoticeAnalytics, type NoticeRowSection } from "./notice-analytics";
import { NoticeFlatList } from "./notice-flat-list";
import { NoticeListSkeleton } from "./notice-list-skeleton";
import type { NoticeConversation } from "./notice-conversations";
import { resolveNoticeDisplay, type NoticeDisplay } from "./notice-display";
import { NoticeFilters } from "./notice-filters";
import { NoticeMessage } from "./notice-message";
import { NoticeSupportGroupList } from "./notice-support-group-list";
import { useNoticeConversations } from "./use-notice-conversations";
import { useNoticeSidebar } from "./use-notice-sidebar";
import { useWorkflowColumnLayout } from "../use-workflow-columns";
import { useMentorWorkspace } from "./use-workspace";
import { MentorWorkspaceHeader } from "./workspace-header";

const SEARCH_USE_DEBOUNCE_MS = 500;

export function NoticeSidebar() {
  const { activeFilter, setActiveFilter, searchQuery, setSearchQuery } =
    useNoticeSidebar();
  const { noticeCollapsed: collapsed, setNoticeCollapsed } =
    useWorkflowColumnLayout();
  const { workspace } = useMentorWorkspace();
  const { channel: selectedChannel, selectChannel } =
    useMentorWorkflowsSelection();
  const { view, loading, error } = useNoticeConversations(workspace);
  const { trackSearchUse, trackBadgeView, trackRowOpen } = useNoticeAnalytics();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const showFilters = !collapsed && view.kind === "flat";

  const display = useMemo(
    () =>
      resolveNoticeDisplay({
        collapsed,
        view,
        loading,
        error,
        activeFilter,
        searchQuery,
      }),
    [collapsed, view, loading, error, activeFilter, searchQuery],
  );

  // Search-use fires on the user's typing event, debounced.
  const searchUseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
      if (searchUseTimer.current !== null) clearTimeout(searchUseTimer.current);
      const trimmed = value.trim();
      if (trimmed === "") return;
      searchUseTimer.current = setTimeout(
        () => trackSearchUse(trimmed),
        SEARCH_USE_DEBOUNCE_MS,
      );
    },
    [setSearchQuery, trackSearchUse],
  );

  useEffect(
    () => () => {
      if (searchUseTimer.current !== null) clearTimeout(searchUseTimer.current);
    },
    [],
  );

  const handleRowOpen = (
    conversation: NoticeConversation,
    position: number,
    section: NoticeRowSection,
  ) => {
    trackRowOpen(conversation, position, section);
    selectChannel(conversation.id, conversation.internalChannelId);
  };

  return (
    <section
      aria-label="Notice"
      className={cn(
        "flex h-full w-full shrink-0 flex-col bg-surfaces-z3 pt-4",
      )}
    >
      {collapsed ? (
        <header className="flex h-14 shrink-0 items-center justify-center border-b border-surfaces-z4 px-3">
          <IconButton
            variant="tertiary"
            size="md"
            shape="circle"
            aria-label="Expand sidebar"
            icon={<PanelLeftOpen className="size-6 text-icon-secondary" />}
            onClick={() => setNoticeCollapsed(false)}
          />
        </header>
      ) : (
        <MentorWorkspaceHeader
          onCollapse={() => {
            setSearchQuery("");
            setNoticeCollapsed(true);
          }}
          onSearchChange={handleSearchChange}
        />
      )}

      {showFilters ? (
        <NoticeFilters
          conversations={view.conversations}
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
        />
      ) : null}

      <div
        ref={scrollRef}
        className="subtle-scrollbar flex flex-1 flex-col gap-0.5 overflow-y-auto px-2 py-1.5"
      >
        <NoticeContent
          display={display}
          collapsed={collapsed}
          selectedChannel={selectedChannel}
          scrollRef={scrollRef}
          onRowOpen={handleRowOpen}
          onBadgeImpression={trackBadgeView}
        />
      </div>

      <NoticeSupportPin collapsed={collapsed} />
    </section>
  );
}

function NoticeSupportPin({ collapsed }: { collapsed: boolean }) {
  const canShowSupport = useShouldShowFloatingChat();
  const { chatPopupOpen, setChatPopupOpen } = usePopupStore();
  const { channels: opsChannels, loading: opsChannelsLoading } =
    useChannelsList({
      customTypesFilter: [...INTERNAL_OPS_CUSTOM_TYPES],
      enabled: canShowSupport,
      enableNotifications: false,
    });
  const hasUnread = useMemo(
    () => opsChannels.some((channel) => channel.unreadMessageCount > 0),
    [opsChannels],
  );

  if (!canShowSupport) return null;

  return (
    <div
      className={cn(
        "shrink-0 border-t border-surfaces-z4 px-2 pb-4 pt-2",
        collapsed && "flex justify-center",
      )}
    >
      <div className="relative">
        {chatPopupOpen ? (
          <div className="absolute bottom-full left-0 z-50 mb-2">
            <ChatPopup
              channels={opsChannels}
              channelsLoading={opsChannelsLoading}
              onClose={() => setChatPopupOpen(false)}
              isOpen={chatPopupOpen}
              position="left"
              isEscalationTriggerVisible={false}
              anchored
            />
          </div>
        ) : null}
        <button
          type="button"
          aria-label={chatPopupOpen ? "Close support" : "Open support"}
          aria-expanded={chatPopupOpen}
          onClick={() => setChatPopupOpen(!chatPopupOpen)}
          className="relative flex size-10 shrink-0 items-center justify-center rounded-full bg-surfaces-z5 font-body text-body-2 font-medium text-text-primary transition-colors hover:bg-surfaces-z4"
        >
          S
          {hasUnread && !chatPopupOpen ? (
            <span className="absolute right-0.5 top-0.5 size-2 rounded-full bg-red-500" />
          ) : null}
        </button>
      </div>
    </div>
  );
}

interface NoticeContentProps {
  display: NoticeDisplay;
  collapsed: boolean;
  selectedChannel: string | null;
  scrollRef: RefObject<HTMLDivElement | null>;
  onRowOpen: (
    conversation: NoticeConversation,
    position: number,
    section: NoticeRowSection,
  ) => void;
  onBadgeImpression: (
    conversation: NoticeConversation,
    position: number,
  ) => void;
}

function NoticeContent({
  display,
  collapsed,
  selectedChannel,
  scrollRef,
  onRowOpen,
  onBadgeImpression,
}: NoticeContentProps) {
  switch (display.kind) {
    case "error":
      return (
        <NoticeMessage>
          Couldn’t load conversations. Try refreshing.
        </NoticeMessage>
      );
    case "loading":
      return <NoticeListSkeleton collapsed={collapsed} />;
    case "empty":
      return (
        <NoticeMessage>
          {display.searching ? "No results found" : "No conversations to show"}
        </NoticeMessage>
      );
    case "grouped":
      return (
        <NoticeSupportGroupList
          groups={display.groups}
          hideEmptyGroups={display.hideEmptyGroups}
          selectedChannel={selectedChannel}
          onRowOpen={(conversation, position) =>
            onRowOpen(conversation, position, "grouped")
          }
        />
      );
    case "flat":
      return (
        <NoticeFlatList
          scrollRef={scrollRef}
          conversations={display.conversations}
          collapsed={collapsed}
          selectedChannel={selectedChannel}
          showBadge={display.showBadge}
          onRowOpen={(conversation, position) =>
            onRowOpen(conversation, position, "flat")
          }
          onBadgeImpression={onBadgeImpression}
        />
      );
    default: {
      const exhaustiveCheck: never = display;
      return exhaustiveCheck;
    }
  }
}
